import { useEffect, useState, type FormEvent } from "react";
import { collection, doc, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { Inbox, Trash2 } from "lucide-react";
import {
  INTAKE_CREATING_TYPES,
  INTAKE_DOC_LABELS,
  M365_ADMIN_CONSENT_URL,
  guessIntakeJobType,
  paths,
  type IntakeItem,
  type IntakeRoute,
  type Mailbox,
} from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, Select } from "@clevis/shared-ui";
import { useJobTypes } from "../../hooks/useJobTypes";
import { useDepartments } from "../../hooks/useDepartments";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { ROLE_RANK } from "@clevis/shared-types";
import { QueueCard, type IntakeItemRow } from "../../components/IntakeQueueCard";

// Email intake: the approval queue every monitored mailbox feeds. Each email
// arrives with what Clevis thinks it is and what to do about it (create,
// update, mark done, cancel, note); the reviewer keeps, edits or retargets
// each action, then applies. Mailboxes and key-phrase rules are
// admin/owner-only; managers see the queue only when the owner shared it
// (Firestore rules enforce both). Work-orders uploads land here too.

type MailboxRow = Mailbox & { id: string };

export function IntakePage() {
  const { tenantId, role } = useAuth();
  const isAdmin = !!role && ROLE_RANK[role] >= ROLE_RANK.admin;
  const [items, setItems] = useState<IntakeItemRow[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!tenantId) return;
    const q = query(
      collection(db, paths.tenantIntakeItems(tenantId)),
      where("status", "==", "pending"),
      orderBy("createdAt", "desc"),
    );
    return onSnapshot(
      q,
      (snap) => setItems(snap.docs.map((d) => ({ id: d.id, ...(d.data() as IntakeItem) }))),
      (err) => setError(err.message),
    );
  }, [tenantId]);

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Email intake</h1>
        <span className="text-xs text-steel">
          {items.length === 0 ? "Queue is clear" : `${items.length} waiting for approval`}
        </span>
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}

      {items.map((item) => (
        <QueueCard key={item.id} item={item} />
      ))}
      {items.length === 0 ? (
        <Card className="py-10 text-center text-sm text-steel">
          <Inbox className="mx-auto mb-2 opacity-40" />
          Nothing waiting. Matched emails show up here for approval.
        </Card>
      ) : null}

      {isAdmin ? <IntakeSettings /> : null}
      {isAdmin && tenantId ? <IntakeRoutes tenantId={tenantId} /> : null}
    </div>
  );
}

// ---- What new jobs from email become: job type + department per email type ----

function IntakeRoutes({ tenantId }: { tenantId: string }) {
  const jobTypes = useJobTypes(tenantId);
  const dept = useDepartments();
  const [saved, setSaved] = useState<Record<string, IntakeRoute>>({});
  const [draft, setDraft] = useState<Record<string, IntakeRoute> | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  useEffect(
    () => onSnapshot(doc(db, paths.tenantIntakeSettings(tenantId)), (snap) => setSaved((snap.get("routes") as Record<string, IntakeRoute> | undefined) ?? {})),
    [tenantId],
  );
  // What applies today: the saved choice, else Clevis's guess from the job type names.
  const current = (t: string): IntakeRoute => (draft ?? saved)[t] ?? { jobType: guessIntakeJobType(t as never, jobTypes), departmentId: null };
  const set = (t: string, patch: Partial<IntakeRoute>) => {
    setNote("");
    setDraft((d) => ({ ...(d ?? saved), [t]: { ...current(t), ...patch } }));
  };
  async function save() {
    if (!draft) return;
    setBusy(true);
    try {
      const routes = Object.fromEntries(INTAKE_CREATING_TYPES.map((t) => [t, current(t)]));
      await call("setIntakeSettings", { tenantId, routes });
      setDraft(null);
      setNote("Saved. New jobs from email start this way; you can still change each one before applying.");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card>
      <CardTitle className="mb-0">New jobs from email</CardTitle>
      <p className="mb-2 text-xs text-steel">
        The job type and department a job gets, by what kind of email made it. A warranty request always makes its own new job,
        even when the house's install job is on file.
      </p>
      <div className="divide-y divide-steel/10">
        {INTAKE_CREATING_TYPES.map((t) => (
          <div key={t} className="flex flex-wrap items-center gap-2 py-1.5">
            <span className="w-36 shrink-0 text-sm text-ink">{INTAKE_DOC_LABELS[t]}</span>
            <Select className="w-44 py-1 text-xs" value={current(t).jobType ?? ""} aria-label={`Job type for ${INTAKE_DOC_LABELS[t]}`} onChange={(e) => set(t, { jobType: e.target.value || null })}>
              <option value="">No job type</option>
              {jobTypes.map((j) => (
                <option key={j} value={j}>
                  {j}
                </option>
              ))}
            </Select>
            {dept.departments.length > 0 ? (
              <Select className="w-56 py-1 text-xs" value={current(t).departmentId ?? ""} aria-label={`Department for ${INTAKE_DOC_LABELS[t]}`} onChange={(e) => set(t, { departmentId: e.target.value || null })}>
                <option value="">No department</option>
                {dept.departments.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </Select>
            ) : null}
            {!saved[t] && !draft?.[t] ? <span className="text-[11px] text-steel">guessed</span> : null}
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Button className="px-3 py-1 text-xs" busy={busy} disabled={!draft} onClick={() => void save()}>
          Save
        </Button>
        {draft ? (
          <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setDraft(null)}>
            Undo
          </Button>
        ) : null}
        {note ? <span className="text-xs text-steel">{note}</span> : null}
      </div>
    </Card>
  );
}

// ---- Admin-only setup: mailboxes, key phrases, manager sharing ------------

function IntakeSettings() {
  const { tenantId } = useAuth();
  const [mailboxes, setMailboxes] = useState<MailboxRow[]>([]);
  const [phrases, setPhrases] = useState<string[]>([]);
  const [phraseInput, setPhraseInput] = useState("");
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", host: "", port: "" });
  const [msBusy, setMsBusy] = useState(false);
  // Shown when a company's Microsoft 365 requires an admin to approve Clevis first.
  const [needsAdmin, setNeedsAdmin] = useState(false);
  const [copied, setCopied] = useState(false);

  // Back from Microsoft sign-in (m365OAuthCallback adds ?m365=…&reason=…): say what happened, then clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("m365");
    if (!result) return;
    const reason = params.get("reason") ?? "";
    const MESSAGES: Record<string, string> = {
      connected: "Microsoft mailbox connected. New matching mail lands in the queue within about 10 minutes.",
      admin_consent_granted: "Clevis is approved for your organization. Now click Connect Microsoft 365 / Outlook to add the mailbox.",
      needs_admin: "Your organization requires a Microsoft 365 admin to approve Clevis first. Send them the approval link below; once they approve, connect again.",
      denied: "Microsoft sign-in was canceled. Nothing was connected.",
      expired: "The sign-in took too long to finish. Click Connect Microsoft 365 / Outlook again.",
      unauthorized: "Only owners and admins can connect mailboxes.",
    };
    setNote(result === "error" ? (MESSAGES[reason] ?? `Microsoft sign-in didn't finish (${reason || "unknown"}). Try again; if it keeps failing, contact support.`) : (MESSAGES[result] ?? ""));
    if (reason === "needs_admin") setNeedsAdmin(true);
    params.delete("m365");
    params.delete("reason");
    const rest = params.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${rest ? `?${rest}` : ""}`);
  }, []);

  async function connectMicrosoft() {
    if (!tenantId) return;
    setMsBusy(true);
    setNote("");
    try {
      const res = await call<{ url: string }>("getM365ConnectUrl", { tenantId }, { mutation: false });
      window.location.href = res.url; // Microsoft sign-in, then back here
    } catch (err) {
      setNote((err as Error).message);
      setMsBusy(false);
    }
  }

  async function copyAdminLink() {
    try {
      await navigator.clipboard.writeText(M365_ADMIN_CONSENT_URL);
      setCopied(true);
    } catch {
      setNeedsAdmin(true); // clipboard blocked: the link is shown to copy by hand
    }
  }

  useEffect(() => {
    if (!tenantId) return;
    const unsubs = [
      onSnapshot(collection(db, paths.tenantMailboxes(tenantId)), (snap) =>
        setMailboxes(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Mailbox) }))),
      ),
      onSnapshot(doc(db, paths.tenantIntakeSettings(tenantId)), (snap) =>
        setPhrases(((snap.get("phrases") as string[] | undefined) ?? []).filter(Boolean)),
      ),
      onSnapshot(doc(db, paths.tenant(tenantId)), (snap) => setShared(snap.get("intakeShared") === true)),
    ];
    return () => unsubs.forEach((u) => u());
  }, [tenantId]);

  async function savePhrases(next: string[]) {
    if (!tenantId) return;
    setPhrases(next);
    await call("setIntakeSettings", { tenantId, phrases: next }).catch((err) => setNote((err as Error).message));
  }

  async function addMailbox(e: FormEvent) {
    e.preventDefault();
    if (!tenantId) return;
    setBusy(true);
    setNote("");
    try {
      const res = await call<{ host: string }>("connectMailbox", {
        tenantId,
        email: form.email.trim(),
        password: form.password,
        ...(form.host.trim() ? { host: form.host.trim() } : {}),
        ...(form.port.trim() ? { port: parseInt(form.port, 10) } : {}),
      });
      setNote(`Connected via ${res.host}. New matching mail lands in the queue within ~10 minutes.`);
      setForm({ email: "", password: "", host: "", port: "" });
      setShowAdd(false);
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardTitle className="mb-0">Monitoring setup</CardTitle>
        <label className="flex cursor-pointer items-center gap-2 text-xs text-ink">
          <input
            type="checkbox"
            checked={shared}
            onChange={(e) => {
              setShared(e.target.checked);
              if (tenantId)
                void call("setIntakeSettings", { tenantId, shareWithManagers: e.target.checked }).catch((err) =>
                  setNote((err as Error).message),
                );
            }}
          />
          Share the queue with managers
        </label>
      </div>
      <p className="mt-1 text-xs text-steel">
        Watched mailboxes are checked every 10 minutes for emails containing any key phrase. Matches are parsed and
        queued above — nothing is created without approval, and the inbox is never modified.
      </p>

      <div className="mt-3">
        <Label>Key phrases</Label>
        <div className="flex flex-wrap items-center gap-1.5">
          {phrases.map((p) => (
            <span key={p} className="flex items-center gap-1 rounded-full bg-steel/15 px-2 py-0.5 text-xs">
              {p}
              <button
                type="button"
                className="text-steel hover:text-danger"
                onClick={() => void savePhrases(phrases.filter((x) => x !== p))}
              >
                ×
              </button>
            </span>
          ))}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const v = phraseInput.trim();
              if (v && !phrases.includes(v)) void savePhrases([...phrases, v]);
              setPhraseInput("");
            }}
          >
            <Input
              className="h-7 w-44 text-xs"
              placeholder="e.g. Purchase Order"
              value={phraseInput}
              onChange={(e) => setPhraseInput(e.target.value)}
            />
          </form>
        </div>
        {phrases.length === 0 ? (
          <p className="mt-1 text-xs text-brandorange-dark">Add at least one phrase — with none, mailboxes aren't scanned.</p>
        ) : null}
      </div>

      <div className="mt-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label className="mb-0">Mailboxes</Label>
          <div className="flex flex-wrap gap-1.5">
            <Button className="px-3 py-1 text-xs" busy={msBusy} onClick={() => void connectMicrosoft()}>
              Connect Microsoft 365 / Outlook
            </Button>
            <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setShowAdd((v) => !v)}>
              {showAdd ? "Cancel" : "Add Gmail / other mailbox"}
            </Button>
          </div>
        </div>
        <p className="mb-1 text-[11px] text-steel">
          Microsoft&apos;s sign-in may show Clevis as an &ldquo;unverified&rdquo; app &mdash; that&apos;s expected for
          now. Clevis only gets read-only access: it can&apos;t send, move or delete mail.
        </p>
        <p className="mb-1 text-[11px] text-steel">
          Microsoft 365 at a company that requires IT approval?{" "}
          <button type="button" className="underline hover:text-ink" onClick={() => void copyAdminLink()}>
            {copied ? "Approval link copied" : "Copy the admin approval link"}
          </button>{" "}
          and send it to your Microsoft 365 admin; they approve Clevis once for everyone.
        </p>
        {needsAdmin ? (
          <p className="mb-1 break-all rounded-lg bg-brandorange/10 p-2 text-[11px] text-ink">{M365_ADMIN_CONSENT_URL}</p>
        ) : null}
        {mailboxes.map((m) => (
          <div key={m.id} className="flex items-center justify-between border-b border-steel/10 py-1.5 text-sm last:border-0">
            <div>
              <span className="font-medium text-ink">{m.email}</span>
              <span className="ml-2 text-xs text-steel">{m.auth === "oauth" ? "Microsoft sign-in" : m.host}</span>
              {m.status === "error" ? (
                <span className="ml-2 text-xs text-danger">
                  {m.lastError ? m.lastError.slice(0, 160) : "connection error"}
                  {m.auth === "oauth" ? (
                    <button type="button" className="ml-1 underline hover:text-ink" onClick={() => void connectMicrosoft()}>
                      Reconnect
                    </button>
                  ) : null}
                </span>
              ) : (
                <span className="ml-2 text-xs text-success">
                  {m.lastPollAt ? `checked ${new Date(m.lastPollAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : "connected"}
                </span>
              )}
            </div>
            <button
              type="button"
              className="text-steel hover:text-danger"
              onClick={() => {
                if (tenantId) void call("removeMailbox", { tenantId, mailboxId: m.id }).catch((err) => setNote((err as Error).message));
              }}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {mailboxes.length === 0 && !showAdd ? <p className="text-xs text-steel">No mailboxes connected yet.</p> : null}
        {showAdd ? (
          <form onSubmit={addMailbox} className="mt-2 grid grid-cols-2 gap-2">
            <div>
              <Label>Email address</Label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <Label>App password</Label>
              <Input
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            <div>
              <Label>IMAP host (blank = auto-discover)</Label>
              <Input placeholder="imap.example.com" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} />
            </div>
            <div>
              <Label>Port</Label>
              <Input placeholder="993" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} />
            </div>
            <p className="col-span-2 text-xs text-steel">
              Gmail: create an app password at myaccount.google.com → Security → App passwords. Microsoft 365 /
              Outlook mailboxes use Connect Microsoft 365 / Outlook above instead (no password needed).
            </p>
            <div className="col-span-2">
              <Button type="submit" busy={busy}>
                Connect
              </Button>
            </div>
          </form>
        ) : null}
      </div>
      {note ? <p className="mt-2 break-words text-xs text-ink">{note}</p> : null}
    </Card>
  );
}
